/**
 * Перенос тренировки на другую дату.
 *
 * Зачем. Тренировки пропускают — день рождения, болезнь, командировка. До
 * этой панели пропущенная тренировка навсегда оставалась просроченной, а весь
 * дальнейший план уезжал в прошлое пачкой: человек либо бросал программу,
 * либо начинал врать приложению, отмечая сделанным то, чего не делал.
 *
 * Два действия, и разница между ними существенная:
 *   - перенести одну (пропустил вторник, доделаю в субботу);
 *   - сдвинуть её и все следующие (выпал на неделю — весь остаток плана
 *     обязан уехать на ту же неделю).
 *
 * Ни одного решения панель не принимает: что и куда поедет, считает движок
 * (`engine.schedule`), а ответ сервера приходит вместе с флагами — сдвоенные
 * даты, размер сдвига, дата в прошлом. Панель их только показывает.
 */

import { useState } from 'react'

import { api, NetworkError } from '../api/client'
import type { RescheduleResult, Session } from '../api/types'
import { haptic } from '../auth/telegram'
import { formatDate } from './ui'

/** Прибавить дни к дате в виде `YYYY-MM-DD`, оставшись в этом же виде.
 *
 * Через `Date` с явным полуднем: `new Date('2026-09-12')` разбирается как
 * полночь UTC, и в поясах западнее Гринвича дата уезжает на день назад ещё
 * до всякой арифметики. */
export function addDays(iso: string, days: number): string {
  const base = new Date(`${iso}T12:00:00`)
  base.setDate(base.getDate() + days)
  const month = String(base.getMonth() + 1).padStart(2, '0')
  const day = String(base.getDate()).padStart(2, '0')
  return `${base.getFullYear()}-${month}-${day}`
}

interface Shortcut {
  label: string
  date: string
}

/** Быстрые варианты: то, что в зале нажимают вместо возни с календарём. */
function shortcuts(session: Session, today: string | null): Shortcut[] {
  const out: Shortcut[] = []
  const from = session.planned_for_date
  // «Сегодня» — самый частый случай: человек пришёл в зал в другой день.
  // Дата берётся только у сервера: у него часовой пояс пользователя, и своя
  // «сегодня» клиента в декабре под Новый год разошлась бы с серверной.
  if (today !== null && today !== from) out.push({ label: 'Сегодня', date: today })
  if (from !== null) {
    out.push({ label: '+1 день', date: addDays(from, 1) })
    out.push({ label: '+2 дня', date: addDays(from, 2) })
    out.push({ label: '+1 неделя', date: addDays(from, 7) })
  }
  return out
}

export default function Reschedule({
  session,
  today,
  onMoved,
  onCancel,
}: {
  session: Session
  /** «Сегодня» глазами сервера. `null` — кнопки «Сегодня» не будет. */
  today: string | null
  onMoved: (result: RescheduleResult) => void
  onCancel: () => void
}) {
  const [date, setDate] = useState<string>(session.planned_for_date ?? today ?? '')
  const [shift, setShift] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const quick = shortcuts(session, today)
  // Сдвиг считается от разницы дат: та же дата — нулевое смещение, и галочка
  // «и следующие» тоже ничего не сделает. Кнопка об этом говорит прямо, а не
  // отправляет запрос, который вернётся с «двигать было нечего».
  const unchanged = date === session.planned_for_date

  async function submit(): Promise<void> {
    if (busy || date === '') return
    setBusy(true)
    setError(null)
    try {
      const result = await api.patch<RescheduleResult>(
        `/sessions/${session.id}/schedule`,
        { planned_for_date: date, shift_following: shift },
      )
      haptic('medium')
      onMoved(result)
    } catch (e) {
      setError(describe(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <div className="row between" style={{ marginBottom: 10 }}>
        <b>Перенести тренировку</b>
        <button
          className="btn ghost"
          style={{ minHeight: 40, padding: '0 12px' }}
          onClick={onCancel}
        >
          Отмена
        </button>
      </div>

      <div className="faint" style={{ marginBottom: 8 }}>
        {session.planned_for_date === null
          ? 'Сейчас у неё нет даты'
          : `Сейчас: ${formatDate(session.planned_for_date)}`}
      </div>

      {quick.length > 0 && (
        <div className="goal-picker">
          {quick.map((item) => (
            <button
              key={item.label}
              type="button"
              className={`pill ${item.date === date ? 'accent' : ''}`}
              onClick={() => setDate(item.date)}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      <div className="faint" style={{ margin: '12px 0 6px' }}>Или выбери дату</div>
      <input
        className="goal-date"
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
      />

      {/* Чекбокс, а не вторая кнопка: это не «другой перенос», а признак того
          же самого. Человек, выпавший на неделю, ставит галочку один раз. */}
      <label className="row" style={{ gap: 10, alignItems: 'flex-start', marginTop: 14 }}>
        <input
          type="checkbox"
          checked={shift}
          onChange={(e) => setShift(e.target.checked)}
          style={{ width: 22, height: 22, marginTop: 2 }}
        />
        <span>
          Сдвинуть и все следующие
          <div className="faint">
            Если выпал на неделю: остальные тренировки уедут на столько же дней.
            Сделанные останутся на своих датах.
          </div>
        </span>
      </label>

      {error && <div className="flag critical" style={{ marginTop: 12 }}>{error}</div>}

      <button
        className="btn primary wide big"
        style={{ marginTop: 14 }}
        disabled={busy || date === '' || unchanged}
        onClick={() => void submit()}
      >
        {busy ? 'Переношу…' : unchanged ? 'Дата не изменилась' : 'Перенести'}
      </button>
    </div>
  )
}

/**
 * Текст ошибки.
 *
 * Разница между «нет связи» и «сервер отказал» здесь принципиальна: перенос,
 * в отличие от подхода, в офлайн-очередь не попадает — при обрыве связи он
 * просто не сохранён, и человек должен это понимать.
 */
function describe(e: unknown): string {
  if (e instanceof NetworkError) return 'Нет связи — перенос не сохранён. Попробуй ещё раз.'
  if (e instanceof Error && e.message) return e.message
  return 'Не удалось перенести тренировку'
}
